import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, Plus, ExternalLink, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function googleCalUrl(apt) {
  const start = `${(apt.date || "").replace(/-/g, "")}T${(apt.time || "09:00").replace(/:/g, "")}00`;
  const dur = apt.duration_min || 30;
  const h = Math.floor(dur / 60); const m = dur % 60;
  const endDate = new Date(`${apt.date}T${apt.time || "09:00"}`);
  endDate.setMinutes(endDate.getMinutes() + dur);
  const end = `${endDate.toISOString().replace(/[-:]/g, "").slice(0, 15)}00`;
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(apt.title)}&dates=${start}/${end}&details=${encodeURIComponent(apt.notes || "")}`;
}

export default function CRMAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", title: "", date: "", time: "09:00", duration_min: "30", notes: "" });

  const fetch = async () => {
    const [a, c] = await Promise.all([
      axios.get(`${API}/crm/appointments`, { withCredentials: true }),
      axios.get(`${API}/crm/clients`, { withCredentials: true }),
    ]);
    setAppointments(a.data);
    setClients(c.data);
  };

  useEffect(() => { fetch(); }, []);

  const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.client_id || !form.title || !form.date) { toast.error("Client, title, and date are required"); return; }
    try {
      await axios.post(`${API}/crm/appointments`, { ...form, duration_min: parseInt(form.duration_min) || 30 }, { withCredentials: true });
      toast.success("Appointment scheduled");
      setDialogOpen(false);
      setForm({ client_id: "", title: "", date: "", time: "09:00", duration_min: "30", notes: "" });
      fetch();
    } catch { toast.error("Error creating appointment"); }
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/crm/appointments/${id}`, { status }, { withCredentials: true });
    fetch();
  };

  const deleteApt = async (id) => {
    await axios.delete(`${API}/crm/appointments/${id}`, { withCredentials: true });
    toast.success("Appointment deleted");
    fetch();
  };

  const STATUS_COLORS = { scheduled: "bg-blue-100 text-blue-800", completed: "bg-green-100 text-green-800", cancelled: "bg-gray-100 text-gray-500" };

  return (
    <div data-testid="crm-appointments" className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Appointments</h1>
        <button onClick={() => setDialogOpen(true)} className="inline-flex items-center gap-1.5 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 px-4 py-2 rounded-sm text-sm">
          <Plus className="w-4 h-4" /> Schedule
        </button>
      </div>

      {appointments.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-sm">
          <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No appointments yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {appointments.map((a) => (
            <div key={a.id} className="bg-white border border-gray-200 rounded-sm p-4 flex items-center gap-4">
              <div className="w-14 h-14 bg-gray-50 border border-gray-200 rounded-sm flex flex-col items-center justify-center flex-shrink-0">
                <p className="text-xs text-gray-500 leading-none">{a.date ? new Date(a.date + "T12:00").toLocaleDateString("en", { month: "short" }) : ""}</p>
                <p className="text-lg font-heading font-bold text-gray-900 leading-none">{a.date ? new Date(a.date + "T12:00").getDate() : ""}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{a.title}</p>
                <p className="text-xs text-gray-500">{a.client_name} {a.time ? `at ${a.time}` : ""} {a.duration_min ? `(${a.duration_min}min)` : ""}</p>
                {a.notes && <p className="text-xs text-gray-400 mt-0.5 truncate">{a.notes}</p>}
              </div>
              <Badge className={`${STATUS_COLORS[a.status]} border-none text-xs`}>{a.status}</Badge>
              <div className="flex items-center gap-1">
                {a.status === "scheduled" && (
                  <>
                    <button onClick={() => updateStatus(a.id, "completed")} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-sm" title="Mark complete"><Check className="w-4 h-4" /></button>
                    <button onClick={() => updateStatus(a.id, "cancelled")} className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-sm" title="Cancel"><X className="w-4 h-4" /></button>
                  </>
                )}
                <a href={googleCalUrl(a)} target="_blank" rel="noreferrer" className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm" title="Add to Google Calendar"><ExternalLink className="w-4 h-4" /></a>
                <button onClick={() => deleteApt(a.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule Appointment</DialogTitle><DialogDescription>Book a meeting, call, or visit with a client.</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Client *</label>
              <Select value={form.client_id} onValueChange={v => handleChange("client_id", v)}>
                <SelectTrigger><SelectValue placeholder="Select client..." /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-semibold text-gray-700 mb-1 block">Title *</label><Input value={form.title} onChange={e => handleChange("title", e.target.value)} placeholder="e.g., Follow-up call, Site visit..." /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-sm font-semibold text-gray-700 mb-1 block">Date *</label><Input type="date" value={form.date} onChange={e => handleChange("date", e.target.value)} /></div>
              <div><label className="text-sm font-semibold text-gray-700 mb-1 block">Time</label><Input type="time" value={form.time} onChange={e => handleChange("time", e.target.value)} /></div>
              <div><label className="text-sm font-semibold text-gray-700 mb-1 block">Duration (min)</label><Input type="number" value={form.duration_min} onChange={e => handleChange("duration_min", e.target.value)} /></div>
            </div>
            <div><label className="text-sm font-semibold text-gray-700 mb-1 block">Notes</label><Textarea value={form.notes} onChange={e => handleChange("notes", e.target.value)} /></div>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm border border-gray-200">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 text-sm font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm">Schedule</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
